import { createContext, PropsWithChildren, ReactNode, useCallback, useMemo, useState } from 'react'

import AlertDialog from './components/alert-dialog'
import ConfirmDialog from './components/confirm-dialog'

export type Dialog = {
  open: boolean
  message?: ReactNode | string
  severity?: 'primary' | 'error' | 'warning'
  confirm?: ReactNode | string
  cancel?: ReactNode | string
  startIcon?: ReactNode
  endIcon?: ReactNode
  resolve: (_value?: unknown) => void
  reject: () => void
}

export type DialogState = {
  alert?: Dialog
  confirm?: Dialog
}

export const DialogsContext = createContext<
  | {
      alert: (_dialog: Partial<Dialog>) => Promise<unknown>
      confirm: (_dialog: Partial<Dialog>) => Promise<unknown>
    }
  | undefined
>(undefined)

const DialogsProvider = ({ children }: PropsWithChildren<unknown>) => {
  const [dialog, setDialog] = useState<DialogState>({})

  /**
   * @desc Dialogs actions.
   */
  const alert = useCallback(
    (dialog: Partial<Dialog>) =>
      new Promise((resolve, reject) =>
        setDialog({ alert: { ...dialog, resolve, reject, open: true } })
      ),
    []
  )
  const confirm = useCallback(
    (dialog: Partial<Dialog>) =>
      new Promise((resolve, reject) =>
        setDialog({ confirm: { ...dialog, resolve, reject, open: true } })
      ),
    []
  )

  /**
   * @desc Dialogs handlers.
   */
  const handleAlertClose = useCallback(() => {
    if (!dialog.alert) return
    setDialog({ alert: { ...dialog.alert, open: false } })
    dialog.alert.resolve()
  }, [dialog.alert])

  const handleConfirmClose = useCallback(
    (value: boolean) => {
      if (!dialog.confirm) return
      setDialog({ confirm: { ...dialog.confirm, open: false } })
      value ? dialog.confirm.resolve() : dialog.confirm.reject()
    },
    [dialog.confirm]
  )

  const handleExited = useCallback(
    () =>
      setDialog({
        alert: undefined,
        confirm: undefined
      }),
    []
  )

  /**
   * @desc Dialogs context.
   */
  const contextValue = useMemo(
    () => ({
      alert,
      confirm
    }),
    [alert, confirm]
  )

  return (
    <DialogsContext.Provider value={contextValue}>
      {children}
      {dialog.alert && (
        <AlertDialog
          {...dialog.alert}
          open={dialog.alert.open}
          onClose={handleAlertClose}
          onExited={handleExited}
        />
      )}
      {dialog.confirm && (
        <ConfirmDialog
          {...dialog.confirm}
          open={dialog.confirm.open}
          onClose={handleConfirmClose}
          onExited={handleExited}
        />
      )}
    </DialogsContext.Provider>
  )
}

export default DialogsProvider
